import { useState, useEffect } from "react";

/**
 * Custom hook for persisting state to localStorage with automatic synchronization.
 *
 * @param {string} key - The localStorage key
 * @param {*} defaultValue - Default value if nothing in storage
 * @returns {[*, function]} - [value, setValue] tuple
 */
export function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      // Try to parse as JSON, fallback to raw string
      try {
        return JSON.parse(stored);
      } catch {
        return stored;
      }
    } catch {
      return defaultValue;
    }
  });

  // Persist to localStorage when value changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (value === null || value === undefined) {
        localStorage.removeItem(key);
      } else {
        const toStore = typeof value === "string" ? value : JSON.stringify(value);
        localStorage.setItem(key, toStore);
      }
    } catch {
      // Ignore storage errors (quota exceeded, etc.)
    }
  }, [key, value]);

  return [value, setValue];
}

/**
 * Custom hook for boolean localStorage values with string storage ("true"/"false").
 * Useful for legacy compatibility where booleans were stored as strings.
 *
 * @param {string} key - The localStorage key
 * @param {boolean} defaultValue - Default boolean value
 * @returns {[boolean, function]} - [value, setValue] tuple
 */
export function useLocalStorageBool(key, defaultValue) {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      if (stored === "true") return true;
      if (stored === "false") return false;
      return defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(key, String(value));
    } catch {
      // Ignore storage errors
    }
  }, [key, value]);

  return [value, setValue];
}

/**
 * Custom hook for numeric localStorage values.
 *
 * @param {string} key - The localStorage key
 * @param {number} defaultValue - Default numeric value
 * @param {object} options - Optional constraints
 * @param {number} options.min - Minimum allowed value
 * @param {number} options.max - Maximum allowed value
 * @returns {[number, function]} - [value, setValue] tuple
 */
export function useLocalStorageNumber(key, defaultValue, options = {}) {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      const parsed = parseInt(stored, 10);
      if (!Number.isFinite(parsed)) return defaultValue;
      if (options.min !== undefined && parsed < options.min) return defaultValue;
      if (options.max !== undefined && parsed > options.max) return defaultValue;
      return parsed;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(key, String(value));
    } catch {
      // Ignore storage errors
    }
  }, [key, value]);

  return [value, setValue];
}

export default useLocalStorage;
